/**
 * Pure client-side pipeline helpers for <DataTable>: filter → search →
 * sort. Extracted from DataTable.jsx so they can be unit-tested directly
 * (a `.jsx` module can't be imported by `node --test`) and so the JSX file
 * exports only its component (Fast-Refresh friendly). Behaviour is
 * unchanged — DataTable imports these verbatim.
 *
 * Column / filter shapes (subset of the DataTable config):
 *   column: { key, sortAccessor?(row), searchAccessor?(row) }
 *   filter: { key, type, filterAccessor?(row) }   // checkbox|radio|select|daterange|text
 * Every accessor falls back to `row[key]` when omitted.
 */

/** True when `row` passes ALL active filters in `config`/`values`. */
export function matchesFilters(row, config, values) {
  for (const f of config) {
    const v = values?.[f.key]
    const acc = f.filterAccessor || ((r) => r[f.key])
    if (f.type === 'checkbox') {
      if (Array.isArray(v) && v.length && !v.includes(acc(row))) return false
    } else if (f.type === 'radio' || f.type === 'select') {
      if (v && acc(row) !== v) return false
    } else if (f.type === 'daterange') {
      if (v && (v.from || v.to)) {
        const d = acc(row)
        if (v.from && d < v.from) return false
        if (v.to && d > v.to) return false
      }
    } else if (f.type === 'text') {
      if (v && !String(acc(row) ?? '').toLowerCase().includes(v.toLowerCase()))
        return false
    }
  }
  return true
}

/** True when `row` matches the free-text query `q`. A blank / <2-char query
 *  matches everything (so the list doesn't churn on the first keystroke).
 *  `scope` restricts the match to one column key; 'all'/falsy searches every
 *  column via its searchAccessor → sortAccessor → row[key]. */
export function matchesSearch(row, q, scope, scopes, columns) {
  const needle = (q || '').trim().toLowerCase()
  if (needle.length < 2) return true
  const cols =
    !scope || scope === 'all'
      ? columns
      : columns.filter((c) => c.key === scope)
  return cols.some((c) => {
    const acc = c.searchAccessor || c.sortAccessor || ((r) => r[c.key])
    return String(acc(row) ?? '').toLowerCase().includes(needle)
  })
}

/** Stable multi-key sort. `sort` is an ordered list of `{ key, dir }`
 *  (dir: 'asc' | 'desc'); earlier keys take precedence, later keys break
 *  ties. Numbers compare numerically, Dates by time, everything else by
 *  locale-aware string compare; null/undefined sort as an empty string.
 *  Returns a NEW array (never mutates the input). */
export function multiSort(rows, sort, columns) {
  if (!sort.length) return rows
  const accs = new Map(
    columns.map((c) => [c.key, c.sortAccessor || ((r) => r[c.key])]),
  )
  const arr = [...rows]
  arr.sort((a, b) => {
    for (const s of sort) {
      const acc = accs.get(s.key) || ((r) => r[s.key])
      let A = acc(a)
      let B = acc(b)
      if (A == null) A = ''
      if (B == null) B = ''
      let cmp
      if (typeof A === 'number' && typeof B === 'number') cmp = A - B
      else if (A instanceof Date && B instanceof Date) cmp = A - B
      else cmp = String(A).localeCompare(String(B))
      if (cmp !== 0) return cmp * (s.dir === 'asc' ? 1 : -1)
    }
    return 0
  })
  return arr
}
